import { Component, EventEmitter, Input, Output, OnChanges, TemplateRef } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import flattenDepth from 'lodash/flattenDepth';

import {
  getStockQuantity,
  IUpsellProductType,
  getDailyLimitQuantity,
  getEffectiveMaxQuantity,
  PRODUCT_TYPE_STRUCTURE_TYPES,
} from '@cakeiteasy/web-utils';

import {
  applySelectionLimit,
  calcCountForProductsGroup,
  getDefaultSelection,
  getExtraProductGroup,
} from '../../cake-builder.helper';
import { ADDONS_TYPES, IUpsellCartLinesGroup } from '../../../cart';
import { IMappedUpsellProductType, IMappedUpsellVariant } from '../../interfaces';

@Component({
  standalone: false,
  selector: 'upsell-products-section',
  templateUrl: './upsell-products-section.component.html',
  styleUrls: ['./upsell-products-section.component.scss']
})
export class UpsellProductsSectionComponent implements OnChanges {
  @Input() upsellProductType: IUpsellProductType;
  @Input() upsellLinesGroup: IUpsellCartLinesGroup;
  @Input() addonsAvailable = true;
  @Input() currency = 'kr';
  @Input() headerTpl: TemplateRef<any> = null;

  @Output() onSelect = new EventEmitter<{ productTypeId: number, productId: number, count: number }>();
  @Output() onAddonsUpdated = new EventEmitter<{
    productTypeId: number,
    addonsLine: IUpsellCartLinesGroup['addonsLine'] | null
  }>();

  public mappedProduct: IMappedUpsellProductType;
  public disablePlus = false;
  public maxQuantity: number | null = null;
  public stockLeft: number | null = null;
  public dailyLimit: number | null = null;
  public sortingAddons: any[] = [];
  public addonsCartLine: any = { questionsMap: {}, extraProductsMap: {}, imagesForPrinting: [] };
  public printingPictureMap: { [key: number]: any } = {};

  constructor(private _translate: TranslateService) {
  }

  ngOnChanges() {
    this.maxQuantity = getEffectiveMaxQuantity(this.upsellProductType);
    this.stockLeft = getStockQuantity(this.upsellProductType);
    this.dailyLimit = getDailyLimitQuantity(this.upsellProductType);
    this.mappedProduct = this._mapUpsellProductTypes(this.upsellProductType);

    if (this.addonsAvailable) {
      this.sortingAddons = this._getSortingAddons(this.upsellProductType.sizes[0]);
      this.addonsCartLine = this._receiveAddonsCartLine(this.upsellLinesGroup);
      this.printingPictureMap = this._toPrintingPictureMap(this.addonsCartLine.imagesForPrinting);
    }

    this.checkPlusBtn(this.getVariantsCount());
  }

  public calcProductCountWrapper(productCount: number): (shiftCount: number, isManual?: boolean) => number {
    return (shiftCount: number, isManual = false) => {
      return calcCountForProductsGroup({
        productCount,
        shiftCount,
        groupCount: this.getVariantsCount(),
        maxQuantity: this.maxQuantity,
        minQuantity: this.upsellProductType.minQuantity,
        isManual,
      });
    };
  }

  public countUpdated(variant: IMappedUpsellVariant, productCount: number) {
    variant.count = productCount;

    this.checkPlusBtn(this.getVariantsCount());
    this.onSelect.emit({ productTypeId: this.upsellProductType.id, productId: variant.productId, count: productCount });
  }

  public getVariantsCount(): number {
    return this.mappedProduct.variants
      .reduce((acc, { count }) => (acc += count, acc), 0);
  }

  public checkPlusBtn(groupCount: number): void {
    this.disablePlus = this.maxQuantity !== null && this.maxQuantity <= groupCount;
  }

  public onPrintingPictureSelected(event: { addOn: any; printingPicture: any }) {
    const { addOn, printingPicture } = event;
    const current = (this.addonsCartLine.imagesForPrinting || []).filter((picture) => picture.id !== addOn.id);

    if (printingPicture && printingPicture.id) {
      current.push(printingPicture);
    }

    this.addonsCartLine.imagesForPrinting = current;
    this.printingPictureMap = this._toPrintingPictureMap(current);
    this.emitAddonsUpdated();
  }

  public onExtraProductSelected(event: { products: any[]; groupId: number }) {
    const { products, groupId } = event;
    this.addonsCartLine.extraProductsMap[groupId] = products;
    this.emitAddonsUpdated();
  }

  public onQuestionAnswerChange(event: { answer: string; question: any }) {
    const { answer, question } = event;
    this.addonsCartLine.questionsMap[question.id] = { ...question, answer };
    this.emitAddonsUpdated();
  }

  private _mapUpsellProductTypes(upsellProductType: IUpsellProductType): IMappedUpsellProductType {
    const { name, description, increment, image, structureType } = upsellProductType;
    const isPieces = structureType === PRODUCT_TYPE_STRUCTURE_TYPES.PIECES;

    return {
      name, description, increment, image,
      variants: flattenDepth(upsellProductType.sizes
        .map(size => size.products
          .map(product => ({
            price: isPieces ? size.price : product.price,
            name: isPieces ? name : product.variant.name,
            image: product.image,
            description: product.variant.description,
            count: this._getProductCount(product.id),
            productId: product.id,
          }))), 2)
    };
  }

  private _getProductCount(productId: number): number {
    const line = this.upsellLinesGroup && this.upsellLinesGroup.lines && this.upsellLinesGroup.lines[productId];
    return line && line.count || 0;
  }

  private _receiveAddonsCartLine(upsellLinesGroup: IUpsellCartLinesGroup) {
    const sourceLine = upsellLinesGroup && upsellLinesGroup.addonsLine || {} as NonNullable<IUpsellCartLinesGroup['addonsLine']>;
    const sourceQuestionsMap = sourceLine.questionsMap || {};
    const sourceExtraProductsMap = sourceLine.extraProductsMap || {};

    const questionsMap = this.sortingAddons
      .filter((addOn) => addOn.addonType === ADDONS_TYPES.QUESTION)
      .reduce((acc, question) => {
        acc[question.id] = {
          ...question,
          ...(sourceQuestionsMap[question.id] || {}),
          question: question.id,
          addonType: ADDONS_TYPES.QUESTION
        };
        return acc;
      }, {});

    const extraProductsMap = this.sortingAddons
      .filter((addOn) => addOn.addonType === ADDONS_TYPES.EXTRA_PRODUCT)
      .reduce((acc, group) => {
        const existing = sourceExtraProductsMap[group.id];

        if (existing) {
          acc[group.id] = applySelectionLimit(group, existing);
          return acc;
        }

        if (group.maxSelections === 1 && group.type === 'YES_NO') {
          acc[group.id] = getDefaultSelection(group.items);
        }

        return acc;
      }, {});

    return {
      size: this.upsellProductType.sizes[0],
      questionsMap,
      extraProductsMap,
      imagesForPrinting: sourceLine.imagesForPrinting || []
    };
  }

  private _getSortingAddons(size: any): any[] {
    if (!size) {
      return [];
    }

    const printingPictures = (size.printingPictures || []).map((picture) => ({
      ...picture,
      addonType: ADDONS_TYPES.PRINTING_PICTURE
    }));

    const questions = (size.questions || []).map((question) => ({
      ...question,
      addonType: ADDONS_TYPES.QUESTION
    }));

    const extraProductGroups = (size.extraGroups || [])
      .map((group) => this._getExtraProductGroup(group))
      .filter((group) => group.items.length > 0)
      .map((group) => ({ ...group, addonType: ADDONS_TYPES.EXTRA_PRODUCT }));

    const requiredCustomerPictures = [];
    const notRequiredCustomerPictures = [];
    printingPictures.forEach((picture) => picture.requiredForCustomer
      ? requiredCustomerPictures.push(picture)
      : notRequiredCustomerPictures.push(picture));

    const addOns = [
      ...extraProductGroups,
      ...questions,
      ...notRequiredCustomerPictures,
    ]
      .filter((addOn) => Boolean(addOn))
      .sort((a, b) => a.priority - b.priority);

    return [...requiredCustomerPictures, ...addOns];
  }

  private emitAddonsUpdated() {
    const { questionsMap, extraProductsMap, imagesForPrinting } = this.addonsCartLine;

    this.onAddonsUpdated.emit({
      productTypeId: this.upsellProductType.id,
      addonsLine: {
        size: this.upsellProductType.sizes[0],
        questionsMap: { ...(questionsMap || {}) },
        extraProductsMap: { ...(extraProductsMap || {}) },
        imagesForPrinting: [...(imagesForPrinting || [])]
      }
    });
  }

  private _toPrintingPictureMap(images: any[]): { [key: number]: any } {
    return (images || []).reduce((acc, picture) => {
      acc[picture.id] = picture;
      return acc;
    }, {});
  }

  private _getExtraProductGroup(group: any): any {
    return getExtraProductGroup(group, this._translate.instant('cakeBuilder.extraProductsSection.noChoice'));
  }
}
